<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
// NOTE: Required both imports to make useKeenSlider library work
// Check https://keen-slider.io/docs for official documentation
import { useKeenSlider } from 'keen-slider/vue';

import AppImg from '../../appImg/AppImg.vue';

export interface AppAutoplayGalleryProps {
  imageUrls: string[];
  isActive?: boolean;
  errorImageHandler?: (src: string) => void;
}

const props = defineProps<AppAutoplayGalleryProps>();

const MAX_IMAGES = 5;
const INITIAL_SLIDE = 0;
const SLIDE_DURATION = 2200;

const currentSlideIndex = ref<number>(INITIAL_SLIDE);
const isImageLoaded = ref<boolean[]>([true]);
const opacities = ref<number[]>([]);
const nextSlideTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const imagesToDisplay = computed<string[]>(() =>
  props.imageUrls.slice(0, MAX_IMAGES)
);

// NOTE: Required core funtion to make useKeenSlider library work
const [container, slider] = useKeenSlider({
  slides: imagesToDisplay.value.length,
  loop: true,
  drag: false,
  initial: INITIAL_SLIDE,
  slideChanged: (slider) => {
    currentSlideIndex.value = slider.track.details.rel;
  },
  detailsChanged: (slider) => {
    opacities.value = slider.track.details.slides.map((slide) => slide.portion);
  },
  animationEnded: () => {
    isImageLoaded.value[currentSlideIndex.value] = true;

    const nextIndex = currentSlideIndex.value + 1;

    if (nextIndex < imagesToDisplay.value.length) {
      isImageLoaded.value[nextIndex] = true;
    }
  },
});

function play(): void {
  if (nextSlideTimeout.value) {
    clearTimeout(nextSlideTimeout.value);
  }

  const existingSlider = slider.value;

  if (!existingSlider || !props.isActive || imagesToDisplay.value.length <= 1) {
    return;
  }

  nextSlideTimeout.value = setTimeout(() => {
    existingSlider.next();
    play();
  }, SLIDE_DURATION);
}

function stop(): void {
  if (nextSlideTimeout.value) {
    clearTimeout(nextSlideTimeout.value);
  }

  slider.value?.moveToIdx(0, false, { duration: 0 });
}

// After imagesToDisplay change, wait for the DOM update and then re-initialize the slider.
// This fixes the issue where the slider doesn't show the correct number of images after sorting.
watch(
  () => imagesToDisplay,
  async () => {
    await nextTick();

    if (slider.value) {
      slider.value.update();
      slider.value.moveToIdx(0, false, { duration: 0 });
    }
  }
);

watch(
  () => props.isActive,
  () => {
    if (props.isActive) {
      play();
      if (imagesToDisplay.value.length > 1) {
        isImageLoaded.value[1] = true;
      }
    } else {
      stop();
    }
  }
);
</script>

<template>
  <div class="app-autoplay-gallery">
    <!-- NOTE: Required classes fader and fader__slide to make useKeenSlider library work -->
    <div ref="container" class="fader">
      <div
        v-for="(image, index) in imagesToDisplay"
        :key="index"
        class="fader__slide"
      >
        <div
          :class="[
            'image-container',
            {
              current: props.isActive && currentSlideIndex === index,
            },
          ]"
        >
          <AppImg
            class="image"
            :src="isImageLoaded[index] ? image : ''"
            :style="{ opacity: opacities[index] }"
            alt="image"
            @img-load-error="props.errorImageHandler"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import url('keen-slider/keen-slider.css');
@import '../../../styles/_media';

.app-autoplay-gallery {
  position: relative;
  height: 100%;

  .fader {
    height: 100%;
    position: relative;
    overflow: hidden;

    .fader__slide {
      height: 100%;
      width: 100%;
      max-width: 100% !important;
      min-width: 100% !important;
      position: absolute;
      top: 0;
    }
  }
  .image-container {
    height: 100%;
    width: 100%;
    aspect-ratio: 4 / 3;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);

    .image {
      transition: transform 0s;
    }

    &.current {
      .image {
        transform: scale(1.06);
        transition: transform v-bind('`${SLIDE_DURATION * 0.9}ms`')
          cubic-bezier(0.25, 0.1, 0.25, 0.9);
      }
    }
  }
}
</style>
